// Karta nové klientky v okně zkušebního tréninku (mockup „Zkušební trénink trenér“,
// varianta A, schváleno 1. 10. 2026). Trenér na jedno klepnutí vidí zdraví, cíl,
// odpovědi z dotazníku a předběžně domluvený pravidelný čas. Data jdou živě z kontaktu
// u sales (GET /api/rezervace?akce=zkusebni_karta), klientka cíl ani zdraví nevidí.
import React from "react";
import { Phone, MessageSquare, HeartPulse, Target, ClipboardList, Repeat, StickyNote } from "lucide-react";
import { Pil, Oznameni } from "./salesUi.jsx";

const MESICE_DM = (iso) => { if (!iso) return ""; const [, m, d] = String(iso).split("-").map(Number); return `${d}. ${m}.`; };
function telHref(t) {
  let p = String(t || "").replace(/[^\d+]/g, "");
  if (!p) return "";
  if (p.startsWith("00")) p = "+" + p.slice(2);
  if (/^\d{9}$/.test(p)) p = "+420" + p;
  else if (/^420\d{9}$/.test(p)) p = "+" + p;
  return p;
}

function Sekce({ ikona: Ikona, nazev, children, zdravi = false }) {
  return (
    <div className={`rounded-xl border px-3 py-2.5 space-y-1.5 ${zdravi ? "border-amber-200 border-l-4 border-l-amber-400 bg-amber-50/60" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-slate-500"><Ikona className="h-4 w-4 shrink-0" />{nazev}</div>
      {children}
    </div>
  );
}
function Radek({ k, v }) {
  if (!v) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 py-1.5 text-sm last:border-b-0">
      <span className="text-slate-500">{k}</span>
      <span className="text-right font-semibold text-slate-800">{v}</span>
    </div>
  );
}
function Dlazdicka({ k, v }) {
  return (
    <div className="rounded-xl border border-slate-200 px-2.5 py-2">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{k}</div>
      <div className="text-[15px] font-bold text-slate-900">{v || "neuvedeno"}</div>
    </div>
  );
}

export default function KartaZkusebniTrener({ karta, trenerId }) {
  if (!karta) return null;
  const k = karta;
  const tel = telHref(k.telefon);
  const maZdravi = !!(k.zdravotni || k.zraneni || k.bolesti);
  const d = k.dotaznik || {};
  const pr = k.pravidelne;
  const uTebe = pr && trenerId && String(pr.trener_id) === String(trenerId);
  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap gap-1.5">
        {k.zaplaceno === null ? null : k.zaplaceno ? <Pil ton="ok" casti="Zaplaceno" /> : <Pil ton="pozor" casti={k.splatnost ? ["Čeká na platbu", `splatnost ${MESICE_DM(k.splatnost)}`] : "Čeká na platbu"} />}
        {d.zkusenosti && <Pil ton="znacka" casti={d.zkusenosti} />}
        <Pil ton="neutralni" casti={k.byvala ? "Bývalá klientka" : "Poprvé u nás"} />
      </div>
      {tel && (
        <div className="grid grid-cols-2 gap-2">
          <a href={`tel:${tel}`} className="btn btn-primary justify-center"><Phone className="h-5 w-5 md:h-4 md:w-4" />Zavolat</a>
          <a href={`sms:${tel}`} className="btn btn-secondary justify-center"><MessageSquare className="h-5 w-5 md:h-4 md:w-4" />SMS</a>
        </div>
      )}
      {(maZdravi || k.zdroj === "sales") && <Sekce ikona={HeartPulse} nazev="Zdraví" zdravi={maZdravi}>
        {maZdravi ? (
          <div>
            <Radek k="Zdravotní omezení" v={k.zdravotni} />
            <Radek k="Zranění" v={k.zraneni} />
            <Radek k="Bolesti" v={k.bolesti} />
          </div>
        ) : <p className="text-sm text-slate-600">Nic neuvedla.</p>}
      </Sekce>}
      {k.cil && <Sekce ikona={Target} nazev="Cíl"><p className="whitespace-pre-line text-[15px] text-slate-800">{k.cil}</p></Sekce>}
      <Sekce ikona={ClipboardList} nazev="Z dotazníku a hovoru">
        <div className="grid grid-cols-2 gap-2">
          <Dlazdicka k="Kolikrát týdně" v={d.frekvence} />
          <Dlazdicka k="Kdy může" v={d.kdy_muze} />
          <Dlazdicka k="Kdy chce začít" v={d.kdy_zacit} />
          <Dlazdicka k="Délka" v={d.delka} />
        </div>
      </Sekce>
      {pr && pr.popis && (
        <Sekce ikona={Repeat} nazev="Pravidelný čas">
          <p className="text-[15px] font-bold text-slate-900">Předběžně {uTebe ? "u tebe" : `u ${pr.trener || "jiného trenéra"}`}: {pr.popis}.</p>
          <p className="text-[13px] text-slate-500">{pr.putujici ? "Putující, místo drží rozvrh trenéra, dokud nezačne." : "Je zapsaný v rozvrhu jako předrezervace. Když po tréninku uzavře spolupráci, potvrdí se."}</p>
        </Sekce>
      )}
      {k.poznamky && <Sekce ikona={StickyNote} nazev={`Poznámka od ${k.domluvila ? k.domluvila.split(" ")[0] : "sales"}`}><p className="whitespace-pre-line text-[15px] text-slate-800">{k.poznamky}</p></Sekce>}
      {k.zdroj === "sales" && <Oznameni ton="znacka">
        {k.domluveno ? `Domluveno ${MESICE_DM(k.domluveno)}${k.domluvila ? `, ${k.domluvila}` : ""}. ` : ""}Klientka dostala e‑mail s pozvánkou a den předem připomínku. Ty jí den předem pošli SMS, připomínka ti přijde na Slack.
      </Oznameni>}
    </div>
  );
}
